import { useSyncHorizontalScroll } from '../../../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../../../hooks/useCalculateFetchItems';
import { useScrollFetchData } from '../../../../hooks/useScrollFetchData';
import MachineReportListViewData from './machineReportListViewData';
import CustomHeaderCell from '../../../../utils/customHeaderCell';
import { useState, useRef, useEffect, useCallback } from 'react';
import { PersistedFixedSizeList as FixedSizeList } from "../../../../hooks/listNavigation";
import { useNavigate } from 'react-router';
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const ReadMachineReportsTab = ({ fromMachinePage = false, machineReportFiltration, setMachineReportFiltration, scrollRef, reports, setOffset, filteredReportsCount, setReportsFetched, sortBy, handleSort }) => {

    const navigate = useNavigate();

    const defaultColumnWidth = {
        controle_date: 250,
        company: 250,
        verifier: 250,
        accompanist: 250,
        status: 250,
        machine: 250,
        document: 250,
    };

    const [machineReportColumnWidth, setMachineReportColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('machineReportColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const machine_status = [
        { id: 'Valide', name: 'Valide' },
        { id: 'AvecReparation', name: 'Avec Reparation' },
        { id: 'NonConforme', name: 'Non Conforme' },
    ];

    const tableHeadRef = useRef(null);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredReportsCount, setReportsFetched, 70, 400);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setReportsFetched, filteredReportsCount, 70, 400);

    const handleReportClick = useCallback((reportData) => {
        navigate(`/dashboard/controles/controle_details/${reportData.id}`, { state: { fromMachinePage } })
    }, [navigate, fromMachinePage]);

    const handleColumnWidthChange = (value, columnKey) => {
        setMachineReportColumnWidth({ ...machineReportColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('machineReportColumnWidth', JSON.stringify(machineReportColumnWidth));
    }, [machineReportColumnWidth]);

    const Row = ({ index, style, data }) => {
        const { reports, handleReportClick } = data;
        const report = index === 0 ? reports[0] : reports[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <MachineReportListViewData
                    report={report}
                    fromMachinePage={fromMachinePage}
                    columnWidth={machineReportColumnWidth}
                    handleReportClick={handleReportClick}
                />
            </Box>
        );
    };

    return (
        <Box>
            <Box className='table'>
                <Box className='tableHead' ref={tableHeadRef}>
                    <CustomHeaderCell
                        label='Date de contrôle'
                        columnKey='controle_date'
                        isFixed={true}
                        backgroundColor='#F6F6F6'
                        sortBy={sortBy}
                        handleSort={handleSort}
                        sortable={true}
                        isFilter={true}
                        value={machineReportFiltration.date}
                        onChange={(e) => { setMachineReportFiltration({ ...machineReportFiltration, date: e }) }}
                        options='date'
                        columnWidth={machineReportColumnWidth}
                        onColumnWidthChange={handleColumnWidthChange}
                    />
                    {!fromMachinePage &&
                        <CustomHeaderCell
                            label='Machine'
                            columnKey='machine'
                            backgroundColor='#F6F6F6'
                            sortBy={sortBy}
                            handleSort={handleSort}
                            sortable={true}
                            columnWidth={machineReportColumnWidth}
                            onColumnWidthChange={handleColumnWidthChange}
                        />
                    }
                    <CustomHeaderCell
                        label='Vérificateur'
                        columnKey='verifier'
                        backgroundColor='#F6F6F6'
                        columnWidth={machineReportColumnWidth}
                        onColumnWidthChange={handleColumnWidthChange}
                    />
                    <CustomHeaderCell
                        label='Accompagnateur'
                        columnKey='accompanist'
                        backgroundColor='#F6F6F6'
                        sortBy={sortBy}
                        handleSort={handleSort}
                        sortable={true}
                        columnWidth={machineReportColumnWidth}
                        onColumnWidthChange={handleColumnWidthChange}
                    />
                    <CustomHeaderCell
                        label='Statut'
                        columnKey='status'
                        backgroundColor='#F6F6F6'
                        sortBy={sortBy}
                        handleSort={handleSort}
                        sortable={true}
                        isFilter={true}
                        value={machineReportFiltration.status}
                        onChange={(e) => { setMachineReportFiltration({ ...machineReportFiltration, status: e.target.value }) }}
                        options={machine_status}
                        columnWidth={machineReportColumnWidth}
                        onColumnWidthChange={handleColumnWidthChange}
                    />
                    <CustomHeaderCell
                        label='Document'
                        columnKey='document'
                        backgroundColor='#F6F6F6'
                        columnWidth={machineReportColumnWidth}
                        onColumnWidthChange={handleColumnWidthChange}
                    />
                    <CustomHeaderCell backgroundColor='#F6F6F6' />
                </Box>
                <Box className='tableBody'>
                    <FixedSizeList
                        ref={scrollRef}
                        height={Math.min(
                            filteredReportsCount * 70 + 15,
                            windowHeight - 400
                        )}
                        width='100%'
                        itemCount={filteredReportsCount}
                        itemSize={70}
                        itemData={{ reports, handleReportClick }}
                        onScroll={handleScrollList}

                    >
                        {Row}
                    </FixedSizeList>
                </Box>
            </Box>
        </Box>
    )
}

export default ReadMachineReportsTab